import { supplyChainSteps, supplyChainStats } from '@/mocks/homeData';
import { useState, useEffect, useRef, useCallback } from 'react';

function CountUpNumber({ end, suffix = '', label, startCounting }: { end: number; suffix?: string; label: string; startCounting: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startCounting) return;
    const duration = 2000;
    let startTime: number | null = null;
    let frame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [end, startCounting]);

  return (
    <div className="text-center">
      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
        {startCounting ? count + suffix : '0' + suffix}
      </div>
      <div className="text-xs text-foreground-500">{label}</div>
    </div>
  );
}

export default function SupplyChain() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const check = useCallback(() => {
    if (hasAnimated || !sectionRef.current) return;
    if (sectionRef.current.getBoundingClientRect().top < window.innerHeight * 0.7) {
      setHasAnimated(true);
    }
  }, [hasAnimated]);

  useEffect(() => {
    check();
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, [check]);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-white">
      <div className="section-container">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-5">
            Supply Chain Strength
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-800 mb-3">
            End-to-End Supply<br />Chain Integration
          </h2>
          <p className="text-foreground-500 max-w-[560px] mx-auto leading-relaxed">
            From design concept to final shipment, our integrated supply chain ensures quality, speed, and complete traceability.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-[50%] w-px h-[calc(100%-96px)] bg-[#E6EAF0] -translate-x-1/2"></div>

          <div className="space-y-0">
            {supplyChainSteps.map((step, i) => (
              <div
                key={i}
                className={`flex items-start gap-6 md:gap-10 py-6 animate-on-scroll animate-on-scroll-delay-${(i % 4) + 1} ${
                  i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${i % 2 === 0 ? 'text-right md:pr-10' : 'text-left md:pl-10'}`}>
                  <div className={`hidden lg:flex items-center gap-3 mb-2 ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-xs font-mono text-accent-400 font-medium">{step.step}</span>
                    <span className="w-6 h-px bg-accent-300"></span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-1.5">{step.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed max-w-[380px] inline-block">{step.description}</p>
                </div>

                <div className="relative z-10 flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading font-semibold text-sm">
                    {step.step}
                  </div>
                </div>

                <div className="flex-1 hidden lg:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 py-8 border-t border-[#E6EAF0] animate-on-scroll">
          {supplyChainStats.map((stat, i) => (
            <CountUpNumber
              key={i}
              end={stat.value}
              suffix={stat.suffix || ''}
              label={stat.label}
              startCounting={hasAnimated}
            />
          ))}
        </div> */}
      </div>
    </section>
  );
}